import { createTheme } from '@mui/material/styles';
import redHatDisplay from 'src/global/fonts/RedHatText-Regular.woff';
import { red, yellow, green, lightBlue, grey } from '@mui/material/colors';

import {
  PRIMARY_PAYMENT_LIGHT,
  WHITE,
  GRAY_FOURTH,
  TERTIARY_PURPLE_DARK,
  BLACK,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  TRANSPARENT,
  MERCHANT_DARK10,
  LAYOUT_BACKGROUND
} from './colors';
import { desktopLG, desktopMD, desktopXL, mobile, tablet } from './breakpoints';

export const modoTheme = createTheme({
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true
      }
    },
    MuiTextField: {
      defaultProps: {
        margin: 'normal',
        variant: 'outlined'
      }
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase': {
            '&:hover': {
              backgroundColor: TRANSPARENT
            }
          }
        }
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          '& .ra-confirm': {
            backgroundColor: PRIMARY_PAYMENT_LIGHT,
            color: WHITE,
            '&:hover': {
              backgroundColor: PRIMARY_PAYMENT_LIGHT,
              color: WHITE
            }
          },
          '& svg': {
            display: 'none'
          }
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiAutocomplete-tag': {
            border: `1px solid ${PRIMARY_PAYMENT_LIGHT}`,
            backgroundColor: 'transparent'
          }
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          color: WHITE,
          backgroundColor: LAYOUT_BACKGROUND
        },
        positionFixed: {
          backgroundColor: PRIMARY_BUTTON,
          color: WHITE
        }
      }
    },
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Red Hat Display';
          font-style: normal;
          font-display: swap;
          font-weight: 400;
          src: local('Red Hat Display'), 
          url(${redHatDisplay}) format('woff');
        }
        * {
          font-family: 'Red Hat Display', 'Roboto', 'Arial', '-apple-system' !important;
        }
      `
    },
    MuiCollapse: {
      styleOverrides: {
        root: {
          '& .RaMenuItemLink-active': {
            borderLeft: `3px solid ${PRIMARY_PAYMENT_LIGHT}`,
            backgroundcolor: 'rgba(0, 0, 0, 0.03) !important'
          }
        }
      }
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: TRANSPARENT,
          borderColor: LAYOUT_BACKGROUND
        }
      }
    }
  },
  palette: {
    mode: 'light',
    primary: {
      main: PRIMARY_PAYMENT_LIGHT,
      light: MERCHANT_DARK10
    },
    secondary: {
      main: SECONDARY_BUTTON,
      contrastText: SECONDARY_BUTTON
    },
    error: {
      light: red[400],
      main: red[500],
      dark: red[300],
      contrastText: WHITE
    },
    success: {
      main: green[500]
    },
    warning: {
      main: yellow[500],
      contrastText: WHITE
    },
    info: {
      main: lightBlue[500]
    },
    text: {
      primary: grey[900],
      secondary: grey[700],
      disabled: grey[500]
    },
    background: {
      default: TRANSPARENT,
      paper: WHITE
    }
  },
  typography: {
    fontFamily: ['Red Hat Display', 'Roboto', 'Arial', '-apple-system'].join(',')
  },
  breakpoints: {
    values: {
      xs: mobile,
      sm: tablet,
      md: desktopMD,
      lg: desktopLG,
      xl: desktopXL
    }
  }
});

export const darkTheme = createTheme({
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true
      }
    },
    MuiTextField: {
      defaultProps: {
        margin: 'normal',
        variant: 'outlined'
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          '& .ra-confirm': {
            backgroundColor: `${PRIMARY_PAYMENT_LIGHT}`,
            color: WHITE,
            '&:hover': {
              backgroundColor: `${PRIMARY_PAYMENT_LIGHT}`,
              color: WHITE
            }
          },
          '& svg': {
            display: 'none'
          }
        }
      }
    },
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Red Hat Display';
          font-style: normal;
          font-display: swap;
          font-weight: 400;
          src: local('Red Hat Display'), 
          url(${redHatDisplay}) format('woff');
        },
        html, body {
          font-family: 'Red Hat Display', 'Roboto', 'Arial', '-apple-system';
        }
      `
    },
    MuiCollapse: {
      styleOverrides: {
        root: {
          '& .RaMenuItemLink-active': {
            borderLeft: `3px solid ${PRIMARY_PAYMENT_LIGHT}`,
            backgroundcolor: 'rgba(0, 0, 0, 0.03) !important'
          }
        }
      }
    }
  },
  palette: {
    mode: 'dark',
    primary: {
      main: PRIMARY_PAYMENT_LIGHT
    }
  },
  typography: {
    fontFamily: ['Red Hat Display', 'Roboto', 'Arial', '-apple-system'].join(',')
  },
  breakpoints: {
    values: {
      xs: mobile,
      sm: tablet,
      md: desktopMD,
      lg: desktopLG,
      xl: desktopXL
    }
  }
});

export const bankTheme = createTheme({
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true
      }
    },
    MuiTextField: {
      defaultProps: {
        margin: 'normal',
        variant: 'outlined'
      }
    },
    
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Red Hat Display';
          font-style: normal;
          font-display: swap;
          font-weight: 400;
          src: local('Red Hat Display'), 
          url(${redHatDisplay}) format('woff');
        }
        html, body {
          fontFamily: 'Red Hat Display', 'Roboto', 'Arial', '-apple-system';
        }
      `
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          '& .ra-confirm': {
            backgroundColor: `${TERTIARY_PURPLE_DARK}`,
            color: WHITE,
            '&:hover': {
              backgroundColor: `${TERTIARY_PURPLE_DARK}`,
              color: WHITE
            }
          },
          '& svg': {
            display: 'none'
          }
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: BLACK
        }
      }
    },
    MuiCollapse: {
      styleOverrides: {
        root: {
          '& .RaMenuItemLink-active': {
            borderLeft: `3px solid ${TERTIARY_PURPLE_DARK}`,
            backgroundcolor: 'rgba(0, 0, 0, 0.03) !important'
          }
        }
      }
    }
  },
  palette: {
    mode: 'light',
    primary: {
      main: TERTIARY_PURPLE_DARK
    },
    secondary: {
      light: TERTIARY_PURPLE_DARK,
      main: TERTIARY_PURPLE_DARK,
      dark: TERTIARY_PURPLE_DARK,
      contrastText: WHITE
    },
    error: {
      light: red[400],
      main: red[500],
      dark: red[300],
      contrastText: WHITE
    },
    success: {
      main: green[500]
    },
    warning: {
      main: yellow[500],
      contrastText: WHITE
    },
    info: {
      main: lightBlue[500]
    },
    text: {
      primary: grey[900],
      secondary: grey[700],
      disabled: grey[500]
    },
    background: {
      default: GRAY_FOURTH,
      paper: WHITE
    }
  },
  typography: {
    fontFamily: ['Red Hat Display', 'Roboto', 'Arial', '-apple-system'].join(',')
  },
  breakpoints: {
    values: {
      xs: mobile,
      sm: tablet,
      md: desktopMD,
      lg: desktopLG,
      xl: desktopXL
    }
  }
});
